import { useState, type ReactNode } from "react";
import { IconAlertTriangle, IconFileTypePdf, IconWorld } from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Group,
  IconAction,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncatedTooltip,
} from "@/components/ui";
import { descargarInvoicePdf } from "@/features/solicitud-detalle/services/invoice-pdf";
import { toastError } from "@/lib/toast";
import { extractApiMessage } from "@/shared/api/extract-message";
import { formatearMonto } from "@/shared/format/money";
import { useInvoiceDeSolicitud } from "../hooks/use-invoice-de-solicitud";
import type { InvoiceDeSolicitud } from "../services/invoice-de-solicitud.api";
import { textoTooltipIndicador, type IndicadorImpuestoResumen } from "../utils/detalle-expandible";

/**
 * Fila de DETALLE de «Mis solicitudes de pago» para el subtipo INVOICE (factura
 * extranjera): en lugar de la sub-tabla de CFDIs muestra, en forma de TABLA, los datos
 * extraídos del invoice y la descarga de su PDF. Mismo tinte gris pizarra y panel con
 * borde/sombra que `FacturasDeSolicitudRow` para que ambas lean igual. Se monta SOLO al
 * expandir (carga perezosa).
 */
export function InvoiceDeSolicitudRow({
  id,
  solicitudId,
  folio,
  colSpan,
}: {
  /** Id del `<tr>` · lo referencia el `aria-controls` del botón de expandir. */
  id: string;
  solicitudId: string;
  folio: string;
  colSpan: number;
}) {
  return (
    <Tr
      id={id}
      className="bg-[color-mix(in_oklab,var(--color-slate-500)_8%,transparent)] hover:bg-[color-mix(in_oklab,var(--color-slate-500)_8%,transparent)]"
    >
      <Td colSpan={colSpan} className="!px-4 !pb-4 !pt-2">
        <section
          aria-label={`Invoice de la solicitud ${folio}`}
          className="overflow-hidden rounded-md border border-[color-mix(in_oklab,var(--color-slate-500)_35%,var(--color-border))] border-l-4 border-l-slate-500 dark:border-l-slate-400 bg-card shadow-md"
        >
          <InvoiceDeSolicitudPanel solicitudId={solicitudId} folio={folio} />
        </section>
      </Td>
    </Tr>
  );
}

/** Equivalente mobile · dentro de la card de la solicitud (misma query que la tabla). */
export function InvoiceDeSolicitudMobile({ id, solicitudId, folio }: { id: string; solicitudId: string; folio: string }) {
  return (
    <div id={id} className="border-t border-border bg-muted/40 px-4 py-3">
      <section
        aria-label={`Invoice de la solicitud ${folio}`}
        className="overflow-hidden rounded-md border border-[color-mix(in_oklab,var(--color-slate-500)_35%,var(--color-border))] border-l-4 border-l-slate-500 dark:border-l-slate-400 bg-card shadow-sm"
      >
        <InvoiceDeSolicitudPanel solicitudId={solicitudId} folio={folio} compacto />
      </section>
    </div>
  );
}

/** Contenido del panel (encabezado + datos) · compartido por desktop y mobile. */
export function InvoiceDeSolicitudPanel({
  solicitudId,
  folio,
  compacto = false,
}: {
  solicitudId: string;
  folio: string;
  compacto?: boolean;
}) {
  const { data, isPending, isError, error, refetch, isFetching } = useInvoiceDeSolicitud(solicitudId);

  let cuerpo: ReactNode;
  if (isPending) {
    cuerpo = (
      <div className="grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4" aria-hidden="true">
        {Array.from({ length: compacto ? 4 : 8 }, (_, i) => (
          <Stack key={i} gap={4}>
            <Skeleton height={10} width="40%" />
            <Skeleton height={14} width="80%" />
          </Stack>
        ))}
      </div>
    );
  } else if (isError) {
    cuerpo = (
      <Group gap="sm" justify="center" className="px-4 py-5" role="status">
        <IconAlertTriangle size={18} className="text-peligro-600" />
        <Text size="sm" className="normal-case">
          No se pudo cargar el invoice: {extractApiMessage(error, "error desconocido")}
        </Text>
        <Button size="xs" variant="default" loading={isFetching} onClick={() => void refetch()}>
          Reintentar
        </Button>
      </Group>
    );
  } else {
    cuerpo = compacto ? (
      <DatosInvoiceCompacto inv={data} />
    ) : (
      <TablasInvoice inv={data} solicitudId={solicitudId} folio={folio} />
    );
  }

  return (
    <>
      <Group
        gap={8}
        justify="space-between"
        wrap="nowrap"
        className="border-b border-[color-mix(in_oklab,var(--color-slate-500)_25%,var(--color-border))] bg-[color-mix(in_oklab,var(--color-slate-500)_14%,var(--color-card))] px-3 py-2"
      >
        <Group gap={8} wrap="nowrap" className="min-w-0">
          <IconWorld size={16} stroke={1.9} className="shrink-0 text-slate-600 dark:text-slate-300" />
          <Text size="xs" fw={700} className="uppercase tracking-wide text-slate-700 dark:text-slate-200" truncate>
            Invoice de la solicitud {folio}
          </Text>
        </Group>
        {/* En mobile no hay columna Acciones: la descarga vive en el encabezado. */}
        {compacto && data?.tienePdf && <BotonDescargaPdf solicitudId={solicitudId} folio={folio} />}
      </Group>
      {cuerpo}
    </>
  );
}

function BotonDescargaPdf({ solicitudId, folio }: { solicitudId: string; folio: string }) {
  const [descargando, setDescargando] = useState(false);
  const descargar = async () => {
    setDescargando(true);
    try {
      await descargarInvoicePdf(solicitudId);
    } catch {
      toastError({ title: "No se pudo descargar", message: "El PDF del invoice no está disponible." });
    } finally {
      setDescargando(false);
    }
  };
  return (
    <IconAction
      label={`Descargar PDF del invoice de la solicitud ${folio}`}
      size="md"
      tone="peligro"
      loading={descargando}
      onClick={() => void descargar()}
    >
      <IconFileTypePdf size={18} stroke={1.9} />
    </IconAction>
  );
}

/**
 * Chip del indicador de impuesto (V0/V5/V7…) en gris pizarra (mismo tinte que el panel),
 * con la tasa y la descripción del catálogo en el tooltip. Sin indicador (tasa null o no
 * catalogada) → «—».
 */
function IndicadorChip({ indicador }: { indicador: IndicadorImpuestoResumen | null }) {
  if (!indicador) return null;
  const tooltip = textoTooltipIndicador(indicador);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge color="slate" variant="light" tabIndex={0} aria-label={`Indicador de impuesto ${tooltip}`}>
          {indicador.codigo}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Detalle del invoice en forma de TABLA (mismo patrón que la sub-tabla de facturas CFDI).
 * NO repite lo que ya muestra la fila padre de «Mis solicitudes»: organización (= receptor)
 * y proveedor del catálogo. El total SÍ va aquí porque la columna Total de la fila padre
 * sale vacía para las solicitudes Invoice (no hay CFDI).
 */
function TablasInvoice({ inv, solicitudId, folio }: { inv: InvoiceDeSolicitud; solicitudId: string; folio: string }) {
  const monto = (v: number) => formatearMonto(v, inv.moneda, { minimumFractionDigits: 2, currencyDisplay: "code" });
  const tooltipEmisor = [inv.emisorNombre, inv.emisorTaxId ? `Tax ID ${inv.emisorTaxId}` : null, inv.emisorDireccion]
    .filter(Boolean)
    .join(" · ");

  return (
    <Table layout="fixed" verticalSpacing="sm" horizontalSpacing="sm">
      <Thead className="bg-muted/60">
        <Tr>
          <Encabezado w="12%">Núm. factura</Encabezado>
          <Encabezado w="11%">Fecha expedición</Encabezado>
          <Encabezado w="28%">Emisor</Encabezado>
          <Encabezado w="11%" align="right">
            Subtotal
          </Encabezado>
          <Encabezado w="11%" align="right">
            Retenciones
          </Encabezado>
          <Encabezado w="9%" align="center">
            Indicador
          </Encabezado>
          <Encabezado w="11%" align="right">
            Total
          </Encabezado>
          <Encabezado w="7%" align="center">
            Acciones
          </Encabezado>
        </Tr>
      </Thead>
      <Tbody>
        <Tr>
          <Td>
            <Text size="sm" fw={600} ff="mono" className="break-all normal-case leading-tight">
              {inv.numeroFactura ?? ""}
            </Text>
          </Td>
          <Td>
            <Text size="sm">{inv.fechaExpedicion ? dayjs(inv.fechaExpedicion).format("DD/MM/YYYY") : ""}</Text>
          </Td>
          <Td>
            <TruncatedTooltip label={tooltipEmisor} disabled={!inv.emisorNombre}>
              <Stack gap={0} className="min-w-0">
                <Text size="sm" fw={600} truncate>
                  {inv.emisorNombre || ""}
                </Text>
                {inv.emisorTaxId && (
                  <Text size="xs" c="dimmed" truncate className="normal-case">
                    Tax ID {inv.emisorTaxId}
                  </Text>
                )}
                {inv.emisorDireccion && (
                  <Text size="xs" c="dimmed" truncate className="normal-case">
                    {inv.emisorDireccion}
                  </Text>
                )}
              </Stack>
            </TruncatedTooltip>
          </Td>
          <Td align="right">
            <Text size="sm" className="whitespace-nowrap">
              {inv.subtotal !== null ? monto(inv.subtotal) : ""}
            </Text>
          </Td>
          <Td align="right">
            <Text size="sm" className="whitespace-nowrap" c={inv.retencionesImporte === null ? "dimmed" : undefined}>
              {inv.retencionesImporte !== null ? monto(inv.retencionesImporte) : ""}
            </Text>
          </Td>
          <Td align="center">
            <IndicadorChip indicador={inv.indicador} />
          </Td>
          <Td align="right">
            <Text size="sm" fw={700} className="whitespace-nowrap">
              {monto(inv.importeTotal)}
            </Text>
          </Td>
          <Td align="center">
            {inv.tienePdf ? (
              <Group justify="center">
                <BotonDescargaPdf solicitudId={solicitudId} folio={folio} />
              </Group>
            ) : null}
          </Td>
        </Tr>
      </Tbody>
    </Table>
  );
}

function Encabezado({ children, w, align }: { children: ReactNode; w: string; align?: "right" | "center" }) {
  return (
    <Th align={align} style={{ width: w }}>
      <Text size="xs" fw={600} className="uppercase leading-tight text-ink-700 dark:text-ink-200">
        {children}
      </Text>
    </Th>
  );
}

/**
 * Mobile · lista etiqueta-valor (una tabla ancha no cabe en la card), sin los campos que ya
 * muestra la card padre (organización/receptor, proveedor). Mismas columnas que la tabla.
 */
function DatosInvoiceCompacto({ inv }: { inv: InvoiceDeSolicitud }) {
  const monto = (v: number) => formatearMonto(v, inv.moneda, { minimumFractionDigits: 2, currencyDisplay: "code" });
  const filas: [string, ReactNode][] = [
    ["Núm. factura", inv.numeroFactura ?? ""],
    ["Fecha expedición", inv.fechaExpedicion ? dayjs(inv.fechaExpedicion).format("DD/MM/YYYY") : ""],
    ["Emisor", [inv.emisorNombre || "", inv.emisorTaxId ? `Tax ID ${inv.emisorTaxId}` : null].filter(Boolean).join(" · ")],
    ["Subtotal", inv.subtotal !== null ? monto(inv.subtotal) : ""],
    ["Retenciones", inv.retencionesImporte !== null ? monto(inv.retencionesImporte) : ""],
    ["Indicador", <IndicadorChip key="indicador" indicador={inv.indicador} />],
    ["Total", monto(inv.importeTotal)],
  ];

  return (
    <dl className="m-0 divide-y divide-border">
      {filas.map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between gap-3 px-3 py-1.5">
          <dt>
            <Text size="xs" c="dimmed" fw={600} className="uppercase tracking-wide">
              {k}
            </Text>
          </dt>
          <dd className="m-0 min-w-0 text-right">
            {typeof v === "string" ? (
              <Text size="sm" fw={k === "Total" ? 700 : 500} className="break-words normal-case">
                {v}
              </Text>
            ) : (
              v
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
